import React, { useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import getSymbolFromCurrency from "currency-symbol-map";
import {
  downloadEventRegistrationsExport,
  EventDashboard,
  EventRegistrationRow,
  getEventDashboard,
  listEventRegistrations,
  listEventRefundRequests,
  RefundRequestRow,
} from "../../api/admin";
import { triggerBlobDownload } from "../../api/orders";
import LoadingPanel from "../../components/LoadingPanel/LoadingPanel";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import EventStatusControl from "../../components/EventStatusControl/EventStatusControl";
import OnsiteRegistrationControl from "../../components/OnsiteRegistrationControl/OnsiteRegistrationControl";
import DiscountCodesPanel from "../../components/DiscountCodesPanel/DiscountCodesPanel";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";
import { CANCELLATION_ADMIN_FEE_USD } from "../../utils/cancellationPolicy";
import { isEventListedAsUpcoming } from "../../utils/eventDates";

type Tab = "overview" | "registrations" | "refunded" | "discounts";

export default function EventAdminPanel() {
  const { eventId } = useParams<{ eventId: string }>();
  const id = Number(eventId);
  const [tab, setTab] = useState<Tab>("overview");
  const [dashboard, setDashboard] = useState<EventDashboard | null>(null);
  const [registrations, setRegistrations] = useState<EventRegistrationRow[]>([]);
  const [refunded, setRefunded] = useState<RefundRequestRow[]>([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const load = async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const [dash, regs, refs] = await Promise.all([
        getEventDashboard(id),
        listEventRegistrations(id, 1, search || undefined),
        listEventRefundRequests(id),
      ]);
      setDashboard(dash);
      setRegistrations(regs.registrations);
      setRefunded(refs);
    } catch (err) {
      setError(
        getUserFacingErrorMessage(err, "Unable to load event panel."),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    const regs = await listEventRegistrations(id, 1, search || undefined);
    setRegistrations(regs.registrations);
  };

  const handleExport = async (format: "csv" | "xlsx") => {
    if (!id) return;
    setIsExporting(true);
    try {
      const blob = await downloadEventRegistrationsExport(id, format);
      triggerBlobDownload(blob, `event-${id}-registrations.${format}`);
    } catch (err) {
      setError(getUserFacingErrorMessage(err, "Export failed."));
    } finally {
      setIsExporting(false);
    }
  };

  if (!id) {
    return <p className="p-8">Invalid event.</p>;
  }

  if (isLoading && !dashboard) {
    return (
      <LoadingPanel
        title="Loading event panel"
        subtitle="Fetching registrations, revenue, and refund data."
        className="py-12"
      />
    );
  }

  if (error && !dashboard) {
    return (
      <div className="max-w-[900px] mx-auto p-8">
        <ErrorAlert className="mb-4">{error}</ErrorAlert>
        <NavLink to="/user/events" className="underline text-sm">
          Back to manage events
        </NavLink>
      </div>
    );
  }

  const currency = "USD";

  return (
    <div className="max-w-[1000px] mx-auto mb-40">
      <NavLink
        to="/user/events"
        className="text-sm text-slate-600 underline mb-4 inline-block"
      >
        ← Back to manage events
      </NavLink>
      <h1 className="font-bold text-xl mb-1">{dashboard?.event.title}</h1>
      {dashboard && (
        <div className="mb-6 max-w-md">
          <p className="text-sm font-medium text-slate-700 mb-2">
            Event visibility
          </p>
          <EventStatusControl
            eventId={dashboard.event.id}
            status={dashboard.event.status}
            onUpdated={(status) =>
              setDashboard((prev) =>
                prev ? { ...prev, event: { ...prev.event, status } } : prev,
              )
            }
          />
        </div>
      )}

      {dashboard && !isEventListedAsUpcoming(dashboard.event) && (
        <div className="mb-6 max-w-md">
          <p className="text-sm font-medium text-slate-700 mb-2">
            On-site registration
          </p>
          <p className="text-xs text-slate-500 mb-2">
            The registration deadline has passed. You can still open
            registration at the on-site price for walk-in / late registrants.
          </p>
          <OnsiteRegistrationControl
            eventId={dashboard.event.id}
            enabled={dashboard.event.onsiteRegistrationOpen}
            onUpdated={(onsiteRegistrationOpen) =>
              setDashboard((prev) =>
                prev
                  ? {
                      ...prev,
                      event: { ...prev.event, onsiteRegistrationOpen },
                    }
                  : prev,
              )
            }
          />
        </div>
      )}

      {error && <ErrorAlert className="mb-4">{error}</ErrorAlert>}

      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 pb-2">
        {(["overview", "registrations", "refunded", "discounts"] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            className={`px-4 py-2 text-sm capitalize ${
              tab === t
                ? "border-b-2 border-slate-800 font-semibold"
                : "text-slate-600"
            }`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "overview" && dashboard && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              label="Confirmed registrations"
              value={String(dashboard.registrations.confirmed)}
            />
            <StatCard
              label="Revenue (registrations)"
              value={`${getSymbolFromCurrency(currency)}${(dashboard.revenue.registrationPaidPence / 100).toFixed(2)}`}
            />
            <StatCard
              label="Refunded"
              value={String(dashboard.refunds.processed)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="border p-4 rounded-sm bg-white">
              <p className="font-semibold mb-2">Registration breakdown</p>
              <p>Pending payment: {dashboard.registrations.pendingPayment}</p>
              <p>Cancelled: {dashboard.registrations.cancelled}</p>
            </div>
            <div className="border p-4 rounded-sm bg-white">
              <p className="font-semibold mb-2">Orders</p>
              <p>Paid orders: {dashboard.revenue.paidOrderCount}</p>
              <p>
                Order revenue:{" "}
                {getSymbolFromCurrency(currency)}
                {(dashboard.revenue.orderPaidPence / 100).toFixed(2)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="eh-btn-primary text-sm"
              disabled={isExporting}
              onClick={() => handleExport("csv")}
            >
              {isExporting ? "Exporting…" : "Download CSV"}
            </button>
            <button
              type="button"
              className="eh-btn-secondary text-sm"
              disabled={isExporting}
              onClick={() => handleExport("xlsx")}
            >
              Download XLSX
            </button>
          </div>
        </div>
      )}

      {tab === "registrations" && (
        <div>
          <form
            onSubmit={handleSearch}
            className="flex flex-col sm:flex-row gap-2 mb-4"
          >
            <input
              type="search"
              placeholder="Search name, email, registration ID…"
              className="border p-2 text-sm w-full sm:flex-1 min-w-0"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="submit"
              className="eh-btn-secondary text-sm w-full sm:w-auto"
            >
              Search
            </button>
            <button
              type="button"
              className="eh-btn-primary text-sm w-full sm:w-auto"
              disabled={isExporting}
              onClick={() => handleExport("csv")}
            >
              CSV
            </button>
          </form>
          <div className="border rounded-sm overflow-x-auto bg-white">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-slate-100 text-left">
                <tr>
                  <th className="p-2">ID</th>
                  <th className="p-2">Name</th>
                  <th className="p-2">Email</th>
                  <th className="p-2">Item</th>
                  <th className="p-2">Paper ID</th>
                  <th className="p-2">Paper title</th>
                  <th className="p-2">Paid</th>
                  <th className="p-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((r) => (
                  <tr key={r.registrationId} className="border-t">
                    <td className="p-2 font-mono text-xs">{r.registrationId}</td>
                    <td className="p-2">
                      {r.firstName} {r.lastName}
                    </td>
                    <td className="p-2">{r.email}</td>
                    <td className="p-2">{r.itemOrdered}</td>
                    <td className="p-2 font-mono text-xs whitespace-pre-wrap break-all">
                      {r.paperId ?? "—"}
                    </td>
                    <td className="p-2 max-w-[16rem] whitespace-pre-wrap break-words" title={r.paperTitle ?? undefined}>
                      {r.paperTitle ?? "—"}
                    </td>
                    <td className="p-2">
                      {(r.amountPaidPence / 100).toFixed(2)} {r.currency}
                    </td>
                    <td className="p-2">
                      {r.registrationDate?.slice(0, 10) ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "refunded" && (
        <div className="space-y-4">
          {refunded.length === 0 ? (
            <p className="text-slate-600 text-sm">No refunded registrations.</p>
          ) : (
            refunded.map((r) => {
              const reg = r.order.registration;
              const symbol = getSymbolFromCurrency(r.order.currency) ?? "$";
              return (
                <div key={r.id} className="border p-4 rounded-sm bg-white text-sm">
                  <div className="flex justify-between gap-4 flex-wrap">
                    <div>
                      {reg && (
                        <p className="font-medium">
                          {reg.firstName} {reg.lastName}
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {reg.registrationId}
                          </span>
                        </p>
                      )}
                      <p className="text-slate-600">
                        {r.order.customerEmail}
                        {reg ? ` · ${reg.itemOrdered}` : ""}
                      </p>
                      <p className="mt-2">
                        Refunded {symbol}
                        {(r.amountPence / 100).toFixed(2)}
                        {r.amountPence === r.order.totalAmountPence ? (
                          <span className="text-slate-600">
                            {" "}
                            (full refund — test pricing)
                          </span>
                        ) : (
                          <>
                            {" "}
                            (original {symbol}
                            {(r.order.totalAmountPence / 100).toFixed(2)}, $
                            {CANCELLATION_ADMIN_FEE_USD} admin fee deducted)
                          </>
                        )}
                      </p>
                      <p className="text-slate-500 text-xs mt-1">
                        Order {r.order.orderNumber ?? "—"} · Processed{" "}
                        {(r.reviewedAt ?? r.createdAt).slice(0, 10)}
                      </p>
                      {r.reason && (
                        <p className="text-slate-500 text-xs mt-1">
                          Reason: {r.reason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {tab === "discounts" && dashboard && (
        <DiscountCodesPanel eventId={dashboard.event.id} />
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="border p-4 rounded-sm bg-white">
      <p className="text-xs text-slate-600 mb-1">{label}</p>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
}
